import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import BrandsSidebar from './BrandsSidebar';
import PartsTable from './PartsTable';
import PartForm from './PartForm';
import { inventoryService } from '../../services/firestore';
import { cacheService } from '../../services/cacheService';
import './Inventory.scss';
import { useTranslation } from 'react-i18next';

function Inventory() {
  const { t } = useTranslation();
  const [showPartForm, setShowPartForm] = useState(false);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const initializeInventory = async () => {
      setLoading(true);
      try {
        await fetchParts();
      } catch (error) {
        console.error('Error initializing inventory:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeInventory();
  }, []);

  const fetchParts = async () => {
    try {
      setLoading(true);
      
      // Prima controlla la cache
      const cachedParts = await cacheService.get('inventory_parts');
      
      if (cachedParts && Array.isArray(cachedParts) && cachedParts.length > 0) {
        setParts(cachedParts);
        organizeBrandsAndModels(cachedParts);
        
        // Se non siamo offline, aggiorna in background
        if (navigator.onLine) {
          const freshParts = await inventoryService.getAll();
          if (JSON.stringify(freshParts) !== JSON.stringify(cachedParts)) {
            await cacheService.set('inventory_parts', freshParts);
            setParts(freshParts);
            organizeBrandsAndModels(freshParts);
          }
        }
        return;
      }

      // Se non c'è cache o è vuota, carica dal server
      const freshParts = await inventoryService.getAll();
      if (Array.isArray(freshParts)) {
        await cacheService.set('inventory_parts', freshParts);
        setParts(freshParts);
        organizeBrandsAndModels(freshParts);
      }
    } catch (error) {
      console.error('Error fetching parts:', error);
      setError('Errore nel caricamento dei ricambi');
    } finally {
      setLoading(false);
    }
  };

  const organizeBrandsAndModels = async (parts = []) => {
    try {
      const brandsMap = new Map();
      
      parts.forEach(part => {
        if (!part.brand || !part.model) return;
        
        if (!brandsMap.has(part.brand)) {
          brandsMap.set(part.brand, new Set());
        }
        brandsMap.get(part.brand).add(part.model);
      });

      const brandsArray = Array.from(brandsMap).map(([name, models]) => ({
        name,
        models: Array.from(models).sort()
      }));

      setBrands(brandsArray);
    } catch (error) {
      console.error('Error organizing brands and models:', error);
      setBrands([]);
    }
  };

  const handleSave = async (newPart) => {
    try {
      // Invalida la cache quando viene aggiunto un nuovo pezzo
      await cacheService.remove('inventory_parts');
      await fetchParts();
      setShowPartForm(false);
    } catch (error) {
      console.error('Error updating after save:', error);
    }
  };

  const filteredParts = parts.filter(part => {
    // Applica prima il filtro di ricerca globale
    const matchesSearch = searchQuery === '' || 
      Object.values(part)
        .join(' ')
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    // Applica il filtro delle scorte basse se attivo
    const matchesLowStock = !showLowStock || 
      (part.minThreshold && part.quantity <= part.minThreshold);

    // Se il filtro scorte basse è attivo, mostra tutti i ricambi con scorte basse
    // indipendentemente dalla marca/modello selezionato
    if (showLowStock) {
      return matchesSearch && matchesLowStock;
    }

    // Se non c'è il filtro scorte basse, applica i filtri normali di marca/modello
    if (selectedModel) {
      return matchesSearch && part.brand === selectedBrand && part.model === selectedModel;
    } else if (selectedBrand) {
      return matchesSearch && part.brand === selectedBrand;
    }
    
    // Se non c'è nessuna selezione e nessun filtro, non mostrare nulla
    return false;
  });

  return (
    <Container fluid className="inventory-container">
      <div className="inventory-box">
        <Row className="g-0">
          <Col md={3} className="sidebar-col">
            <BrandsSidebar
              brands={brands}
              selectedBrand={selectedBrand}
              selectedModel={selectedModel}
              onSelectBrand={setSelectedBrand}
              onSelectModel={setSelectedModel}
            />
          </Col>
          
          <Col md={9} className="content-col">
            <div className="content-header d-flex justify-content-between align-items-center">
              <h2 className="h4 mb-0">
                {selectedModel 
                  ? `${t('inventory.partsFor.prefix')} ${selectedModel}${t('inventory.partsFor.suffix')}`
                  : t('inventory.selectModel')
                }
              </h2>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant={showLowStock ? "warning" : "light"}
                  className={`d-flex align-items-center stock-alert-btn ${showLowStock ? 'active' : ''}`}
                  onClick={() => setShowLowStock(!showLowStock)}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  {t('inventory.buttons.lowStock')}
                </Button>
                <div className="search-container">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="search"
                    className="form-control search-input"
                    placeholder={t('inventory.buttons.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Button 
                  variant="primary" 
                  className="add-item-btn"
                  onClick={() => setShowPartForm(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {t('inventory.buttons.addNew')}
                </Button>
              </div>
            </div>

            <PartsTable 
              parts={filteredParts}
              loading={loading}
              onUpdate={fetchParts}
              selectedModel={selectedModel}
            />
          </Col>
        </Row>
      </div>

      <PartForm 
        show={showPartForm}
        onHide={() => setShowPartForm(false)}
        onSave={handleSave}
      />
    </Container>
  );
}

export default Inventory; 